function initAnimalServicesMap() {
    const root = document.querySelector('.js-as-map')

    if (!root || !root.dataset.regions) {
        return
    }

    let regions

    try {
        regions = JSON.parse(root.dataset.regions)
    } catch (error) {
        return
    }

    const canvas = root.querySelector('.as__map__canvas')
    const tooltip = root.querySelector('.as__map__tooltip')
    const panel = root.querySelector('[data-as-panel]')
    const panelName = root.querySelector('[data-as-panel-name]')
    const panelTier = root.querySelector('[data-as-panel-tier]')
    const panelServices = root.querySelector('[data-as-panel-services]')
    const paths = Array.from(root.querySelectorAll('.as__map__region'))
    const chips = Array.from(root.querySelectorAll('[data-as-chips] [data-region]'))
    const desktopQuery = window.matchMedia('(min-width: 1024px)')

    let selectedKey = root.dataset.defaultRegion || Object.keys(regions)[0]
    let hoveredKey = null
    let tabOrder = []

    try {
        if (root.dataset.tabOrder) {
            const parsed = JSON.parse(root.dataset.tabOrder)

            if (Array.isArray(parsed)) {
                tabOrder = parsed
            }
        }
    } catch (error) {
        tabOrder = []
    }

    let i18n = {
        serviceOne: '%s service',
        serviceMany: '%s services',
    }

    try {
        if (root.dataset.i18n) {
            i18n = { ...i18n, ...JSON.parse(root.dataset.i18n) }
        }
    } catch (error) {
        // Keep English fallbacks if the attribute is malformed.
    }

    const getRegion = (key) => regions[key] || null

    const serviceCountLabel = (count) => {
        const template = count === 1 ? i18n.serviceOne : i18n.serviceMany

        return template.replace('%s', String(count))
    }

    const setPathState = () => {
        paths.forEach((path) => {
            const key = path.dataset.region
            const isSelected = key === selectedKey
            const isHovered = key === hoveredKey

            path.classList.toggle('is-selected', isSelected)
            path.classList.toggle('is-hovered', isHovered && !isSelected)
            path.setAttribute('aria-pressed', isSelected ? 'true' : 'false')
        })

        chips.forEach((chip) => {
            const isSelected = chip.dataset.region === selectedKey

            chip.classList.toggle('is-selected', isSelected)
            chip.setAttribute('aria-pressed', isSelected ? 'true' : 'false')
        })
    }

    const renderPanel = (region) => {
        if (!region || !panel) {
            return
        }

        if (panelName) {
            panelName.textContent = region.name
        }

        if (panelTier) {
            panelTier.textContent = region.tierLabel
            panelTier.className = `as__map__badge as__map__badge--${region.tier}`
        }

        if (panelServices) {
            // Sanitized server-side with wp_kses_post.
            panelServices.innerHTML = region.services
        }
    }

    const hideTooltip = () => {
        if (!tooltip) {
            return
        }

        tooltip.hidden = true
        tooltip.textContent = ''
    }

    const clampTooltip = (clientX, clientY) => {
        if (!tooltip || !canvas) {
            return
        }

        const bounds = canvas.getBoundingClientRect()
        const tip = tooltip.getBoundingClientRect()
        const width = tip.width || 220
        const height = tip.height || 72
        let left = clientX - bounds.left - (width / 2)
        let top = clientY - bounds.top - height - 12

        left = Math.max(8, Math.min(left, bounds.width - width - 8))
        top = Math.max(8, Math.min(top, bounds.height - height - 8))

        tooltip.style.left = `${left}px`
        tooltip.style.top = `${top}px`
    }

    const showTooltip = (region, clientX, clientY) => {
        if (!tooltip || !region) {
            return
        }

        tooltip.replaceChildren()

        const nameEl = document.createElement('strong')
        const countEl = document.createElement('span')

        nameEl.textContent = region.name
        countEl.textContent = serviceCountLabel(region.count)
        tooltip.append(nameEl, countEl)
        tooltip.hidden = false
        clampTooltip(clientX, clientY)
    }

    const selectRegion = (key, options = {}) => {
        const region = getRegion(key)

        if (!region) {
            return
        }

        selectedKey = key
        renderPanel(region)
        setPathState()

        if (options.tooltip && typeof options.clientX === 'number') {
            showTooltip(region, options.clientX, options.clientY)
        }
    }

    const orderedControls = (elements) => {
        const byKey = new Map(elements.map((element) => [element.dataset.region, element]))
        const ordered = []
        const seen = new Set()

        tabOrder.forEach((key) => {
            const element = byKey.get(key)

            if (!element || seen.has(key)) {
                return
            }

            ordered.push(element)
            seen.add(key)
        })

        elements.forEach((element) => {
            const key = element.dataset.region

            if (!key || seen.has(key)) {
                return
            }

            ordered.push(element)
            seen.add(key)
        })

        return ordered
    }

    const activeControls = () => orderedControls(desktopQuery.matches ? paths : chips)

    const isVisible = (element) => {
        if (element.closest('[hidden]')) {
            return false
        }

        let node = element

        while (node && node.nodeType === 1) {
            const style = window.getComputedStyle(node)

            if (style.display === 'none' || style.visibility === 'hidden') {
                return false
            }

            node = node.parentElement
        }

        return true
    }

    const focusOutside = (forward) => {
        const controls = activeControls()
        const group = new Set(controls)
        const selector = [
            'a[href]',
            'button:not([disabled])',
            'input:not([disabled]):not([type="hidden"])',
            'select:not([disabled])',
            'textarea:not([disabled])',
            '[tabindex]:not([tabindex="-1"])',
        ].join(', ')
        const focusable = Array.from(document.querySelectorAll(selector)).filter((element) => {
            if (element.getAttribute('tabindex') === '-1') {
                return false
            }

            return isVisible(element)
        })
        const indexes = []

        focusable.forEach((element, index) => {
            if (group.has(element)) {
                indexes.push(index)
            }
        })

        if (!indexes.length) {
            return
        }

        const target = forward
            ? focusable[Math.max(...indexes) + 1]
            : focusable[Math.min(...indexes) - 1]

        if (target) {
            target.focus()
        }
    }

    const onTab = (event) => {
        if (event.key !== 'Tab' || event.altKey || event.ctrlKey || event.metaKey) {
            return
        }

        const controls = activeControls()
        const index = controls.indexOf(event.currentTarget)

        if (index === -1) {
            return
        }

        const next = controls[index + (event.shiftKey ? -1 : 1)]

        event.preventDefault()

        if (next) {
            next.focus({ preventScroll: true })
            return
        }

        focusOutside(!event.shiftKey)
    }

    const syncTabOrder = () => {
        const desktop = desktopQuery.matches
        const pathOrder = orderedControls(paths)
        const firstKey = pathOrder[0] ? pathOrder[0].dataset.region : ''
        const lastKey = pathOrder.length ? pathOrder[pathOrder.length - 1].dataset.region : ''

        paths.forEach((path) => {
            if (!desktop) {
                path.setAttribute('tabindex', '-1')
                path.setAttribute('aria-hidden', 'true')
                return
            }

            const key = path.dataset.region
            const isEdge = key === firstKey || key === lastKey

            path.removeAttribute('aria-hidden')
            path.setAttribute('tabindex', isEdge ? '0' : '-1')
        })

        chips.forEach((chip) => {
            chip.setAttribute('tabindex', desktop ? '-1' : '0')
        })
    }

    paths.forEach((path) => {
        path.addEventListener('mouseenter', (event) => {
            hoveredKey = path.dataset.region
            selectRegion(hoveredKey, {
                tooltip: true,
                clientX: event.clientX,
                clientY: event.clientY,
            })
        })

        path.addEventListener('mousemove', (event) => {
            const region = getRegion(path.dataset.region)

            if (region) {
                showTooltip(region, event.clientX, event.clientY)
            }
        })

        path.addEventListener('mouseleave', () => {
            hoveredKey = null
            setPathState()
            hideTooltip()
        })

        path.addEventListener('focus', () => {
            const region = getRegion(path.dataset.region)

            if (!region) {
                return
            }

            selectRegion(region.key)

            const box = path.getBoundingClientRect()

            showTooltip(region, box.left + box.width / 2, box.top)
        })

        path.addEventListener('blur', () => {
            hideTooltip()
        })

        path.addEventListener('click', () => {
            path.focus({ preventScroll: true })
            selectRegion(path.dataset.region)
        })

        path.addEventListener('keydown', (event) => {
            if (event.key === 'Tab') {
                onTab(event)
                return
            }

            if (event.key !== 'Enter' && event.key !== ' ') {
                return
            }

            event.preventDefault()
            selectRegion(path.dataset.region)
        })
    })

    chips.forEach((chip) => {
        chip.addEventListener('focus', () => {
            selectRegion(chip.dataset.region)
            hideTooltip()
        })

        chip.addEventListener('click', () => {
            selectRegion(chip.dataset.region)
            hideTooltip()
        })

        chip.addEventListener('keydown', onTab)
    })

    if (typeof desktopQuery.addEventListener === 'function') {
        desktopQuery.addEventListener('change', syncTabOrder)
    } else if (typeof desktopQuery.addListener === 'function') {
        desktopQuery.addListener(syncTabOrder)
    }

    syncTabOrder()
    selectRegion(selectedKey)
}

jQuery(document).ready(function () {
    initAnimalServicesMap()
})
